import moment from 'moment-timezone';
import api from '../services/api';

/**
 * Copia o arquivo escolhido para a memória.
 *
 * No celular, o arquivo vindo da galeria/câmera é uma referência do sistema de
 * arquivos que pode ficar indisponível entre a escolha e o envio (app em segundo
 * plano, arquivo movido pela galeria). Quando isso acontece o navegador não
 * consegue montar o corpo da requisição e o upload falha antes de chegar na API,
 * aparecendo como "Network Error".
 *
 * Em caso de falha na leitura, devolve o arquivo original — é melhor tentar o
 * envio do que impedir o cadastro.
 */
export async function copiaArquivoEmMemoria(arquivo) {
    if (!arquivo) {
        return null;
    }

    try {
        const buffer = await arquivo.arrayBuffer();

        return new File([buffer], arquivo.name, {
            type: arquivo.type,
            lastModified: arquivo.lastModified,
        });
    } catch (e) {
        console.error('Não foi possível copiar o arquivo para a memória:', e);
        return arquivo;
    }
}

/**
 * Alerta de relatório de monitoramento repetido.
 *
 * O backend recusa o PDF quando a sessão dele já foi cadastrada no atendimento, mas
 * responde só com o texto técnico do campo. Quem está na tela precisa saber que o
 * problema é o ARQUIVO — que aquele relatório já tinha sido enviado antes — e o que
 * aconteceu com a confirmação.
 *
 * Devolve null quando o erro é outro, para o chamador seguir com o tratamento padrão.
 *
 * @param {object} error - erro do axios
 * @param {boolean} confirmacaoAbortada - true quando a realização não chegou a ser confirmada
 */
export function alertaPdfMonitoramentoRepetido(error, confirmacaoAbortada = false) {
    if (!error?.response?.data?.monitoramento_pdf) {
        return null;
    }

    const complemento = confirmacaoAbortada
        ? 'A realização <b>não</b> foi confirmada e o serviço não foi gerado.'
        : 'O arquivo não foi enviado.';

    return {
        icon: 'warning',
        title: 'Este relatório já foi enviado',
        html: 'O relatório de monitoramento que você anexou <b>já tinha sido cadastrado</b> '
            + 'neste atendimento — normalmente é o arquivo de uma sessão anterior.'
            + `<br /><br />${complemento}`
            + '<br /><br />Confira se o arquivo é o relatório da sessão deste agendamento e envie novamente.',
        confirmButtonColor: '#3085d6',
        showCloseButton: true,
    };
}

/**
 * Anexa o relatório de telemetria também aos arquivos do agendamento.
 *
 * O relatório é gravado no relato do atendimento (é de lá que os indicadores são
 * lidos), então sem isto ele não aparece em "Arquivos do Agendamento" — que é onde
 * a operação procura o que foi enviado.
 *
 * Falha aqui não interrompe nada: o relatório já está salvo no relato.
 */
export async function anexaTelemetriaAoAgendamento(arquivo, agendamentoId) {
    if (!arquivo || !agendamentoId) {
        return;
    }

    try {
        const formData = new FormData();

        formData.append('arquivo', arquivo);
        formData.append('nome', `Telemetria - ${arquivo.name}`);
        formData.append('agendamento_id', agendamentoId);
        formData.append('data', moment().tz('America/Sao_Paulo').format('YYYY-MM-DD HH:mm:ss'));
        formData.append('tipo', 'monitoramento');
        formData.append('pessoa_id', 1);
        formData.append('aprovado', 'true');  // não entra no fluxo de aprovação de laudo

        await api.post('/agendamentos/arquivos/', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    } catch (e) {
        console.error('Não foi possível anexar a telemetria aos arquivos do agendamento:', e);
    }
}
